import { FC, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import useDataInitializer from "../../hooks/useDataInitializer";
import { useDebounce } from "../../hooks/useDebounce";
import useListFilters from "../../hooks/useListFilters";
import { useI18n } from "../../hooks/useTranslations";
import { useCostImportsApi } from "../../http/costImports/api";
import { CostImport } from "../../http/costImports/types";
import { PaginationQuery } from "../../types";
import { formatDate, formatTimeStamp } from "../../types/utils";
import Header from "../ui/Header";
import Panel from "../ui/Panel";
import Table from "../ui/Table";

import { useUserSettings } from "../../context/userSettingsContext";
import SvgCancel from "../icons/Cancel";
import SvgCheckCircle from "../icons/CheckCircle";
import { Check } from "../ui/Check";
import { getDeleteAction } from "../ui/ListItemMenu";
import CostsImportButton from "./CostsImportButton";
import DeleteCostImportDialog from "./DeleteCostImportDialog";

const CostImports: FC = () => {
  const i18n = useI18n();
  const userSettings = useUserSettings();
  const costImportsApi = useCostImportsApi();
  const filters = useListFilters({ isSearchable: true });
  const debouncedValue = useDebounce(filters.state.q);
  const aborter = useRef(new AbortController());
  const [itemToDelete, setItemToDelete] = useState<CostImport>();

  const navigate = useNavigate();

  const query: PaginationQuery = {
    ...filters.state,
    q: debouncedValue,
  };

  const result = costImportsApi.useCostImports(query, aborter.current.signal);
  const [data] = useDataInitializer(result.data, result.error);

  return (
    <>
      <Header title={i18n.t.common.costImport} />
      <Panel>
        <Table<CostImport>
          filters={filters}
          buttons={{
            main: userSettings.permissions.costsImports.insert && <CostsImportButton />,
          }}
          searchOptions={{
            onChange: () => {
              aborter.current.abort();
              aborter.current = new AbortController();
            },
          }}
          head={
            <>
              <Table.Th title={i18n.t.common.type} fieldName="import.type" />
              <Table.Th title={i18n.t.common.uploadedBy} fieldName="import.uploader.name" />
              <th align="left">{i18n.t.common.uploadedAt}</th>
              <th align="left">{i18n.t.common.countDate}</th>
              <Table.Th title={i18n.t.common.importedInCosts} fieldName="import.importedInCosts" />
              <Table.Th title={i18n.t.subsidiaries.singular} fieldName="" />
              {userSettings.permissions.costsImports.delete && <th></th>}
            </>
          }
          renderRow={(item) => (
            <>
              <td>{item.type}</td>
              <td>{item.uploader.fullName}</td>
              <td>{formatTimeStamp(item.uploadedAt, i18n.language)}</td>
              <td>{formatDate(item.countDate, i18n.language)}</td>
              <td>
                {
                  <Check
                    icon={item.isImportedInCosts ? SvgCheckCircle : SvgCancel}
                    color={item.isImportedInCosts ? "green" : "red"}
                  />
                }
              </td>
              <td>{item.subsidiary.name}</td>
            </>
          )}
          onRowClick={(item) => {
            navigate(`/admin/costs_imports/${item.type}/${item.id}?isImported=${item.isImportedInCosts}`);
          }}
          rowActions={(item) =>
            userSettings.permissions.costsImports.delete ? [getDeleteAction(i18n, () => setItemToDelete(item))] : []
          }
          error={result.error}
          items={data?.items}
          total={data?.total}
        />
      </Panel>

      {!!itemToDelete && (
        <DeleteCostImportDialog cotsImport={itemToDelete} onClose={() => setItemToDelete(undefined)} />
      )}
    </>
  );
};

export default CostImports;
